/**
 * Finnish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal sinä (locked in l10n/locales.md),
 * expressed idiomatically — 2nd-person-singular imperatives (Avaa,
 * Lähetä) and possessive suffixes (laitteesi) rather than an explicit
 * pronoun.
 *
 * Platform terms follow Finnish UI conventions (Teema for theme,
 * keskustelu for conversation, laitepari/parittaa for pair, hylkää for
 * dismiss, vastaanotin for a receiving surface); "Glance" is the
 * untranslated feature brand name (glossary D1) and "leike" is the
 * native clipboard-item word for clip (D2 — Finnish has no established
 * loan form, so the native coinage, parallel to zh).
 *
 * Placeholder rule: Finnish is case-inflected but placeholders never
 * take a suffix — phrasing uses a case-carrying head noun ("laitteelta
 * ${label}") or a postposition instead.
 *
 * Plural notes: Finnish is a {one, other} CLDR locale. Count nouns after
 * a numeral take partitive singular (1 viesti / 5 viestiä), so every
 * count-bearing function selects through `selectPlural("fi", …)`. Where
 * the numeral's case makes both forms coincide (allative "laitteelle",
 * elative "laitteesta"), the two branches are deliberately identical.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Yhdistetty",
    statusReconnecting: "Yhdistetään uudelleen…",
    statusDisconnected: "Yhteys katkaistu",
    openSettings: "Avaa asetukset",
    pairedFallbackDevice: "puhelimesi",
    publicModeToggleEnable: "Piilota ilmoitusten sisältö",
    publicModeToggleDisable: "Näytä ilmoitusten sisältö",
  },

  notifications: {
    replyAriaLabel: "Vastaa",
    replyPlaceholder: "Vastaa…",
    replySend: "Lähetä",
    sent: "Lähetetty",
    failedToSend: "Lähetys epäonnistui",
    unread: "Lukematon",
    dismiss: "Hylkää",
    notificationImageAlt: "Ilmoituksen kuva",
  },

  conversations: {
    replyAriaLabel: "Vastaa",
    replyPlaceholder: "Vastaa…",
    replySend: "Lähetä",
    sent: "Lähetetty",
    failedToSend: "Lähetys epäonnistui",
    dismiss: "Hylkää keskustelu",
    messagesListLabel: "Tämän keskustelun viestit",
    deviceOwner: "Sinä",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Ei lukemattomia viestejä"
        : `${count} ${selectPlural("fi", count, {
            one: "lukematon viesti",
            other: "lukematonta viestiä",
          })}`,
  },

  list: {
    today: "Tänään",
    yesterday: "Eilen",
    searchNotifications: "Hae ilmoituksia",
    clearSearch: "Tyhjennä haku",
    clearAll: "Tyhjennä kaikki",
    markAllRead: "Merkitse kaikki luetuiksi",
    groupCheckboxLabel: "Ryhmittele",
    noNotificationsTitle: "Ei vielä ilmoituksia",
    noNotificationsDesc: "Puhelimestasi peilatut uudet viestit näkyvät täällä.",
    noMatchesTitle: "Ei osumia",
    notificationsList: "Ilmoitukset",
    privacyAppList: "Ilmoitukset sovelluksittain",
    clear: "Tyhjennä",
    clearMatches: (count: number): string =>
      `Tyhjennä ${count} ${selectPlural("fi", count, { one: "osuma", other: "osumaa" })}`,
    noMatchesDesc: (query: string): string => `Mikään ilmoitus ei vastaa hakua ”${query}”.`,
    appNotificationsGroup: (appName: string): string => `Sovelluksen ${appName} ilmoitukset`,
    dismissAllFrom: (appName: string): string => `Hylkää kaikki sovelluksesta ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("fi", count, { one: "viesti", other: "viestiä" })
          : kind === "calls"
            ? selectPlural("fi", count, { one: "puhelu", other: "puhelua" })
            : selectPlural("fi", count, { one: "ilmoitus", other: "ilmoitusta" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "hylkää",
    kbdNavigate: "selaa",
    kbdOrSeparator: "tai",
  },

  settings: {
    back: "Takaisin ilmoituksiin",
    settings: "Asetukset",
    general: "Yleiset",
    theme: "Teema",
    themeAuto: "Automaattinen",
    themeLight: "Vaalea",
    themeDark: "Tumma",
    language: "Kieli",
    languageSystemDefault: "Järjestelmän oletus",
    notificationsSection: "Ilmoitukset",
    osNotifications: "Käyttöjärjestelmän ilmoitukset",
    callNotifications: "Puheluilmoitukset",
    privacySecurity: "Yksityisyys",
    analyticsToggle: "Anonyymit käyttötilastot",
    analyticsToggleDescription: "Vain määrät ja virstanpylväät — ei koskaan ilmoituksiasi.",
    statusConnected: "Yhdistetty",
    statusReconnecting: "Yhdistetään uudelleen…",
    statusDisconnected: "Yhteys katkaistu",
    disconnectFromPhone: "Katkaise yhteys puhelimeen",
    disconnectConfirm: "Oletko varma? Tämä poistaa laitteesi laiteparin.",
    yesDisconnect: "Kyllä, katkaise yhteys",
    cancel: "Peruuta",
    version: "Versio",
    encryption: "Salaus",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Tarvitsetko apua? Kirjoita osoitteeseen __EMAIL__",
    disconnectFailed: "Yhteyden katkaisu epäonnistui — yritä uudelleen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Yhteys katkaistu, mutta paikallinen siivous epäonnistui: ${errorMsg}. Lataa sivu uudelleen.`,
    disconnectLocalCleanupFailed:
      "Yhteys katkaistu, mutta paikallinen siivous epäonnistui. Lataa sivu uudelleen.",
  },

  pairing: {
    enterCodeInstruction: "Syötä tämä koodi Reflecto-sovellukseen",
    pairedSuccessfully: "Laitepari muodostettu!",
    pairingFailed: "Laiteparin muodostaminen epäonnistui",
    requestingPairingCode: "Pyydetään laiteparin koodia…",
    codeExpired: "Koodi vanheni",
    tryAgain: "Yritä uudelleen",
    failedToStartPairing: "Laiteparin muodostamista ei voitu aloittaa",
    installHeading: "Eikö sinulla ole vielä sovellusta?",
    installScanHint: "Skannaa puhelimen kameralla",
    installQrLabel: "QR-koodi Reflecton asentamiseen puhelimeen",
    capabilitiesHeading: "Laiteparin jälkeen voit:",
    capabilityMirror: "Nähdä puhelimen ilmoitukset täällä",
    capabilityReply: "Vastata tarttumatta puhelimeen",
    capabilityShare: "Jakaa leikepöydän ja tiedostoja molempiin suuntiin",
    encryptionNote: "Päästä päähän -salattu. Palvelin ei näe koskaan sisältöäsi.",
  },

  dnd: {
    title: "Älä häiritse",
    description: "Keskeytä ilmoitukset tällä laitteella seuraavan tunnin ajaksi.",
    snooze1h: "Keskeytä 1 tunniksi",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiivinen ${formattedTime} asti`,
    cancelSnooze: "Peruuta",
  },

  connectors: {
    title: "Yhdistetyt palvelut",
    emptyBody:
      "Ei vielä yhdistettyjä palveluita. Yhdistä ChatGPT tai Claude palvelun omista yhdistämisasetuksista ja hyväksy sitten koodi puhelimellasi.",
    scopeSend: "Voi lähettää ilmoituksia",
    added: (date: string): string => `Lisätty ${date}`,
    lastUsed: (relative: string): string => `Viimeksi käytetty ${relative}`,
    neverUsed: "Ei koskaan käytetty",
    revoke: "Katkaise yhteys",
    revokeConfirm: (label: string): string =>
      `Katkaistaanko yhteys palveluun ${label}? Se menettää käyttöoikeuden heti.`,
    revokeAction: "Katkaise yhteys",
    revokeConfirmLabel: (label: string): string => `Katkaise yhteys palveluun ${label}`,
    cancel: "Peruuta",
    loadError: "Yhdistettyjä palveluita ei voitu ladata. Yritä uudelleen.",
    revokeError: "Palvelun yhteyttä ei voitu katkaista. Yritä uudelleen.",
    retry: "Yritä uudelleen",
  },

  safetyNumber: {
    rowLabel: "Vahvista salaus",
    rowSublabel: "Vertaa turvallisuusnumeroita puhelimeesi",
    cardTitle: "Turvallisuusnumero",
    caption: (deviceLabel: string): string =>
      `Vertaa numeroon, joka näkyy laitteessa ${deviceLabel}. Jos molemmat täsmäävät, päätepisteestä päähän -salaus on kunnossa.`,
    repairNote: "Tämä numero muuttuu, kun asetat laitteen uudelleen käyttöön.",
    copy: "Kopioi",
    copied: "Kopioitu",
    maskedHint: "Piilotettu yksityisyystilan ollessa käytössä",
  },

  feedback: {
    title: "Lähetä palautetta",
    close: "Sulje",
    messageLabel: "Viestisi",
    messagePlaceholder:
      "Löysitkö bugin, onko sinulla idea, vai tuntuuko jokin oudolta? Kerro meille.",
    categoryLabel: "Luokka",
    categoryBug: "Bugi",
    categoryIdea: "Idea",
    categoryOther: "Muu",
    emailLabel: "Sähköposti (valinnainen, vain jos haluat vastauksen)",
    emailPlaceholder: "sinä@esimerkki.fi",
    emailHelp: "Emme lisää sinua millekään listalle.",
    disclosure:
      "Viestisi lähetetään Reflecton palvelimelle ja tallennetaan sisäiseen ongelmienseurantaamme. Liitämme mukaan sovellusversiosi ja laiteparin muodostamisen yhteydessä annetun laitetunnuksen, jotta voimme yhdistää raportteja toisiinsa. Emme näe ilmoituksiasi, yhteystietojasi emmekä muita henkilökohtaisia tietoja. Sähköposti on valinnainen — anna se vain, jos haluat vastauksen. Emme lisää sinua millekään listalle.",
    submit: "Lähetä palautetta",
    sending: "Lähetetään…",
    success: "Kiitos — ihminen lukee, yleensä 24 tunnin sisällä",
    error: "Lähetys epäonnistui. Yritä uudelleen.",
    done: "Valmis",
    sendFeedback: "Lähetä palautetta",
  },

  uninstall: {
    headline: "Reflecton laajennus on poistettu",
    body: "Tiliä ei ollut koskaan, ja laajennus säilytti laiteparin tiedot vain tällä tietokoneella, joten siivottavaa ei jäänyt.",
    ask: "Jos sinulla on kolmisenkymmentä sekuntia, kuulisimme mielellämme, mikä meni pieleen.",
    categoryLegend: "Millaista palautetta tämä on?",
    categoryBug: "Jokin meni rikki",
    categoryIdea: "Siitä puuttui jotain, mitä tarvitsin",
    categoryOther: "Jokin muu",
    messageLabel: "Mikä meni pieleen?",
    messagePlaceholder: "Ilmoitukset lakkasivat tulemasta muutaman tunnin jälkeen.",
    messageRequired: "Kirjoita ensin lause ja lähetä sitten.",
    charactersLeft: (count: number): string => `${count} merkkiä jäljellä`,
    emailLabel: "Sähköposti (valinnainen)",
    emailPlaceholder: "sinä@esimerkki.fi",
    emailHelp: "Käytetään vain vastaamiseen. Ei mihinkään muuhun.",
    submit: "Lähetä palautetta",
    sending: "Lähetetään…",
    retry: "Yritä uudelleen",
    privacyNote:
      "Tätä lomaketta ei ole liitetty mihinkään laitteeseen. Se ei sisällä ilmoituksiasi, leikepöytääsi eikä tiedostojasi. Sähköposti on valinnainen ja sitä käytetään vain vastaamiseen.",
    success: "Kiitos. Jokaisen näistä lukee ihminen.",
    errorGeneric: "Viesti ei lähtenyt. Tekstisi on yhä tallessa.",
    errorRateLimited:
      "Olet lähettänyt viime aikoina useita viestejä. Yritä uudelleen noin tunnin kuluttua. Tekstisi säilyy tässä.",
    footerWebApp: "Reflecton verkkosovellus toimii ilman laajennustakin.",
    footerWebAppLink: "Avaa verkkosovellus",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Sulje",
    aiSummaryTag: "Tekoälykooste",
    errorAi: "Yhteenvetoa ei voitu juuri nyt luoda.",
    retryCta: "Yritä uudelleen",
    downloadHeading: "Ota tekoälykoosteet käyttöön",
    downloadBody:
      "Katso tekoälykoosteesta laitteellasi, mitä jäi huomaamatta. Toimii kokonaan laitteellasi.",
    downloadCta: "Lataa malli",
    downloadPrivacyNote: "Kertalataus. Palvelin ei koskaan näe ilmoituksiasi.",
    downloadingHeading: "Ladataan mallia…",
    caughtUp: "Olet ajan tasalla — mikään ei kaipaa huomiotasi juuri nyt.",
    needsYou: "Vaatii huomiota",
    quiet: "Muut",
    scanning: (pending: number): string => `Tiivistetään ${pending}…`,
    jump: "Avaa ilmoitus",
    affordanceReply: "Vastaa",
    affordanceOpen: "Avaa",
    affordanceCopyOtp: "Kopioi koodi",
    buttonLabel: "Glance",
  },

  update: {
    title: "Päivitys saatavilla",
    description: "Uusi Reflecto-versio on valmis. Päivitä lataamalla sivu uudelleen.",
    reload: "Lataa uudelleen",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Nimesimme tämän laitteen nimellä ”${name}”.`,
    cardLooksGood: "Näyttää hyvältä",
    cardEdit: "Muokkaa",
    cardAriaLabel: "Vahvista laitteen nimi",
    renameTitle: "Nimeä tämä laite uudelleen",
    renameLabel: "Laitteen nimi",
    renameSave: "Tallenna",
    renameCancel: "Peruuta",
    renameFailed: "Uudelleennimeäminen epäonnistui. Yritä uudelleen.",
  },

  collision: {
    title: (name: string): string => `Sinulla on jo ”${name}”`,
    subtitle: "Miksi kutsumme tätä?",
    chipWork: (name: string): string => `Työ ${name}`,
    chipHome: (name: string): string => `Koti ${name}`,
    chipPersonal: (name: string): string => `Oma ${name}`,
    customLabel: "Mukautettu nimi",
    save: "Tallenna",
    useAnyway: "Käytä silti alkuperäistä nimeä",
    saveFailed: "Uutta nimeä ei voitu tallentaa. Yritä uudelleen.",
    ariaLabel: "Ratkaise päällekkäinen laitenimi",
  },

  clipboard: {
    title: "Leikepöytä",
    emptyTitle: "Ei vielä leikkeitä",
    empty: "Muiden laitteidesi leikkeet näkyvät täällä. Kopioi napauttamalla.",
    from: (label: string): string => `laitteelta ${label}`,
    to: (label: string): string => `laitteelle ${label}`,
    copy: "Kopioi leikepöydälle",
    copied: "Kopioitu",
    reveal: "Näytä",
    hide: "Piilota",
    maskedHint: "Sisältö piilotettu — näytä painamalla Näytä",
    sensitiveChip: "Arkaluontoinen",
    cloudChip: "API-tokenistasi",
    delete: "Poista leike",
    sendPlaceholder: "Liitä lähetettävä teksti…",
    sendCta: "Lähetä",
    sendSensitiveToggle: "Merkitse arkaluontoiseksi",
    // Numeral + allative: the noun stays "laitteelle" for every count
    // (1 laitteelle / 5 laitteelle) — the branches coincide by grammar.
    sendSuccess: (n: number): string =>
      `Lähetetty ${n} ${selectPlural("fi", n, { one: "laitteelle", other: "laitteelle" })}.`,
    // "2/3 laitteesta" — elative after the fraction; noun agrees with
    // `total`, and both CLDR branches coincide (numeral + singular case).
    sendPartial: (n: number, total: number): string =>
      `Tavoitettu ${n}/${total} ${selectPlural("fi", total, { one: "laitteesta", other: "laitteesta" })}.`,
    sendNoDevices: "Ei paritettuja laitteita, joille lähettää.",
    sendFailed: "Lähetys epäonnistui. Yritä uudelleen.",
    sendEmpty: "Ei mitään lähetettävää.",
    dockRecentHeading: "Viimeisimmät",
    dockEmptyClipboard: "Leikepöytäsi on tyhjä.",
    clearAll: "Tyhjennä kaikki",
    clearAllConfirm: "Kyllä, tyhjennä kaikki",
    clearAllCancel: "Peruuta",
    shareTitle: "Lähetä laitteillesi",
    shareReview:
      "Tarkista, mitä olet lähettämässä. Se kulkee päästä päähän -salattuna paritetuille laitteillesi.",
    shareSend: "Lähetä",
    shareCancel: "Peruuta",
    shareNotPaired: "Muodosta ensin laitepari lähettääksesi jaetun tekstin.",
    shareOpenApp: "Avaa Reflecto",
    pasteFromClipboard: "Liitä leikepöydältä",
    readFailed: "Leikepöytää ei voitu lukea — liitä teksti alle.",
  },
  install: {
    title: "Asenna Reflecto",
    body: "Lisää aloitusnäytölle nopeampaa käyttöä varten.",
    cta: "Asenna",
    dismissAria: "Hylkää asennuskehote",
  },
  nav: {
    home: "Koti",
    clipboard: "Leikepöytä",
    files: "Tiedostot",
    ariaLabel: "Päävalikko",
    fileHiddenPublicMode: "Piilotettu, kun yksityisyystila on käytössä.",
  },
  common: {
    loading: "Ladataan",
  },
  errors: {
    notPaired: "Ei paritettu",
  },
  device: {
    // Composes identically to the extension's deviceNameTemplate
    // "$BROWSER$ ($OS$)": Finnish has no natural "on" joiner that
    // wouldn't inflect the OS name, so the parenthetical form is used
    // ("Chrome (macOS)", 14 chars ≤ 22).
    nameTemplate: (browser: string, os: string) => `${browser} (${os})`,
    webFallback: "Verkkoselain",
  },

  palette: {
    placeholder: "Kirjoita komento…",
    noResults: "Ei vastaavia komentoja",
    ariaLabel: "Komentopaletti",
    clearAll: "Tyhjennä kaikki ilmoitukset",
    glance: "Glance-kooste",
    cancelSnooze: "Peruuta keskeytys",
    copyClip: "Kopioi viimeksi vastaanotettu leike",
  },

  fileTransfer: {
    sendTitle: "Lähetä tiedosto",
    sendChooseFile: "Valitse tiedosto",
    sendDeviceLabel: "Vastaanottaja",
    sendPhoneFallbackName: "Puhelin",
    send: "Lähetä tiedosto",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Valmistellaan…" : `Valmistellaan — ${pct}%`,
    sendDelivering: "Toimitetaan puhelimeesi…",
    sendWaitingAccept: "Odotetaan, että puhelimesi hyväksyy…",
    sendWaitingAppOpen: "Avaa Reflecto puhelimessasi hyväksyäksesi",
    sendWaitingAppOpenHint:
      "Puhelimesi ei voi vastaanottaa push-ilmoituksia, joten se näyttää tämän tiedoston vasta kun Reflecto on auki.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Ladataan osaa ${partNumber}/${partCount}…`,
    sendDone: "Lähetetty.",
    sendFailedRejected: "Puhelimesi hylkäsi tiedoston.",
    sendFailedNoAnswer: "Puhelimesi ei vastannut.",
    sendFailedUploadFailed: "Lataus epäonnistui. Yritä uudelleen.",
    sendFailedCancelled: "Peruutettu.",
    sendFailedGeneric: "Jokin meni pieleen. Yritä uudelleen.",
    sendTooLarge: (maxMiB: number): string => `Tiedosto on liian suuri (enintään ${maxMiB} MiB).`,
    sendKeepOpenHint: "Pidä tämä välilehti auki, kunnes siirto on valmis.",
    sendAnother: "Lähetä toinen",
    sendNoDevice: "Muodosta ensin laitepari puhelimen kanssa lähettääksesi tiedostoja.",
    sendCancel: "Peruuta",
    sendPeerProgress: (chunksDone: number): string =>
      `Puhelin on vastaanottanut ${chunksDone} lohkoa.`,

    receiveTitle: "Saapuvat tiedostot",
    receiveOfferFrom: (sender: string): string => `${sender} haluaa lähettää sinulle tiedoston`,
    receiveAccept: "Hyväksy",
    receiveDecline: "Hylkää",
    receiveReceiving: (n: number, total: number): string => `Vastaanotetaan osaa ${n}/${total}…`,
    receiveDone: "Tallennettu.",
    receiveFailed: "Siirto epäonnistui.",
    receiveCancelled: "Peruutettu.",
    receiveCancel: "Peruuta",

    errorChunkAuthFail: "Siirto epäonnistui — tietoja ei voitu varmentaa. Yritä uudelleen.",
    errorHashMismatch:
      "Siirto epäonnistui — tiedosto ei läpäissyt eheystarkistusta. Yritä uudelleen.",
    errorStorageFull: "Tallennustila ei riitä tähän siirtoon.",
    errorExpired: "Siirto vanheni.",
    errorQuotaExceeded: "Päivittäinen siirtoraja täyttyi — yritä uudelleen huomenna.",
    errorInternal: "Siirto epäonnistui — jokin meni pieleen.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Hylkää",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Siirron edistyminen",
    outgoing: (n: number): string => `Lähtevät (${n})`,
    incoming: (n: number): string => `Saapuvat (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Hyväksy kaikki",
    dropTitle: "Vedä ja pudota tähän",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Tiedostot enintään ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Vedä ja pudota tähän tai valitse lähetettävä tiedosto",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Suora",
    transportDirectHint:
      "Lähetetty laitteelta laitteelle — ei koskaan tallennettu palvelimillemme.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Palvelimen kautta",
    transportRelayHint:
      "Välitetty palvelimiemme kautta, edelleen päästä päähän salattu — emme voi lukea sitä.",
    directBadge: "Suora",
  },
  time: {
    justNow: "juuri nyt",
    minutesAgo: (count: number): string => `${count} min sitten`,
    hoursAgo: (count: number): string => `${count} t sitten`,
  },
} satisfies MessagesShape;
